import { useSearchParams } from "react-router";
import { getFallbackRange } from "../utils/getFallbackRange";
import {
  customRange,
  monthRange,
  nextRange,
  prevRange,
  weekRange,
  yearRange,
  type Range,
} from "../utils/dateRange";

/**
 * The single owner of the selected date range. Holds start/end/interval in URL
 * search params (with the getFallbackRange default) and exposes every mutation
 * — select a preset, pick a custom range, paginate — backed by the pure math in
 * utils/dateRange. Consumers read { start, end, interval } instead of repeating
 * the search-param default block.
 */
export function useDateRange() {
  const [searchParams, setSearchParams] = useSearchParams({
    start: getFallbackRange().start,
    end: getFallbackRange().end,
    interval: "month",
  });

  const start = searchParams.get("start");
  const end = searchParams.get("end");
  const interval = searchParams.get("interval");

  const apply = (range: Range, nextInterval?: string) =>
    setSearchParams(
      (params: any) => {
        params.set("start", range.start);
        params.set("end", range.end);
        if (nextInterval) params.set("interval", nextInterval);
        return params;
      },
      { preventScrollReset: true }
    );

  return {
    start,
    end,
    interval,
    selectWeek: () => apply(weekRange(), "week"),
    selectMonth: () => apply(monthRange(), "month"),
    selectYear: () => apply(yearRange(), "year"),
    selectCustom: (from: any, to: any) => apply(customRange(from, to), "custom"),
    next: () => apply(nextRange(start, end, interval), undefined),
    prev: () => apply(prevRange(start, end, interval), undefined),
  };
}
